import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Check, Users, X, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { formatApiError } from "../lib/AuthContext";
import { useT } from "../lib/i18n";
import { useSiteContent } from "../lib/content";
import { RevealLines } from "../components/Reveal";

const STORAGE_KEY = "joh_member_v1";
const WEEKDAY_NL = ["Maandag","Dinsdag","Woensdag","Donderdag","Vrijdag","Zaterdag","Zondag"];
const WEEKDAY_EN = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

function fmtDate(iso, lang) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const wd = (dt.getUTCDay() + 6) % 7;
  const wdName = (lang === "en" ? WEEKDAY_EN : WEEKDAY_NL)[wd];
  const monthShort = dt.toLocaleString(lang === "en" ? "en-GB" : "nl-NL", { month: "short", timeZone: "UTC" });
  return { weekday: wdName, day: dt.getUTCDate(), month: monthShort };
}

const CREDIT_BADGE = {
  membership: { nl: "Membership", en: "Membership" },
  joh_pack_5: { nl: "Pack 5", en: "Pack 5" },
  joh_pack_10: { nl: "Pack 10", en: "Pack 10" },
  joh_dropin: { nl: "Drop-in", en: "Drop-in" },
  promo_free_session: { nl: "Gratis sessie", en: "Free session" },
  guest: { nl: "Gast", en: "Guest" },
};

function loadMember() {
  try { const raw = localStorage.getItem(STORAGE_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}

export default function MemberPortal() {
  const { lang } = useT();
  const { content } = useSiteContent();
  const trialEnabled = content?.trial_settings?.enabled !== false;
  const [params, setParams] = useSearchParams();
  const [member, setMember] = useState(loadMember());
  const [emailInput, setEmailInput] = useState("");
  const [firstNameInput, setFirstNameInput] = useState("");
  const [lastNameInput, setLastNameInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  const [dropinBusy, setDropinBusy] = useState(false);

  const load = useCallback(async (email) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/me/summary?email=${encodeURIComponent(email)}`);
      setData(data);
    } catch (e) {
      toast.error(lang === "en" ? "Could not load your data" : "Kon je gegevens niet laden");
    }
    setLoading(false);
  }, [lang]);

  useEffect(() => { if (member?.email) load(member.email); }, [member, load]);

  // Auto-adopt member from ?email= (e.g., after Stripe checkout success)
  useEffect(() => {
    const queryEmail = params.get("email");
    if (queryEmail && (!member?.email || member.email.toLowerCase() !== queryEmail.toLowerCase())) {
      const next = { email: queryEmail, name: queryEmail.split("@")[0] };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      setMember(next);
      // Clean the URL so a refresh doesn't re-adopt
      setParams({}, { replace: true });
    }
  }, [params, member, setParams]);

  const signIn = (e) => {
    e.preventDefault();
    if (!emailInput.trim()) return;
    const fn = firstNameInput.trim();
    const ln = lastNameInput.trim();
    const composed = `${fn} ${ln}`.trim() || emailInput.split("@")[0];
    const m = { first_name: fn, last_name: ln, name: composed, email: emailInput.trim().toLowerCase(), phone: "" };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(m));
    setMember(m);
  };

  const signOut = () => {
    localStorage.removeItem(STORAGE_KEY);
    setMember(null); setData(null); setEmailInput(""); setFirstNameInput(""); setLastNameInput("");
  };

  const cancelRsvp = async (instance_id) => {
    if (!member?.email) return;
    try {
      await api.delete(`/classes/rsvp?instance_id=${encodeURIComponent(instance_id)}&email=${encodeURIComponent(member.email)}`);
      toast.success(lang === "en" ? "Cancelled." : "Afgemeld.");
      load(member.email);
    } catch { toast.error(lang === "en" ? "Cancel failed" : "Afmelden mislukt"); }
  };

  const redeemPromo = async (e) => {
    e.preventDefault();
    if (!promoInput.trim() || !member?.email) return;
    setPromoBusy(true);
    try {
      await api.post("/promos/redeem", { code: promoInput.trim(), email: member.email, name: member.name });
      toast.success(lang === "en" ? "Free session added to your account" : "Gratis sessie aan je account toegevoegd");
      setPromoInput("");
      load(member.email);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || (lang === "en" ? "Invalid code" : "Ongeldige code"));
    }
    setPromoBusy(false);
  };

  const bookDropin = async () => {
    // Drop-in vereist registratie + leskeuze — leid door naar de publieke flow
    window.location.href = "/join?plan=joh_dropin";
  };

  if (!member) {
    return (
      <main className="min-h-screen bg-[#F5F3F0] grain flex flex-col" data-testid="me-page">
        <header className="px-6 md:px-10 py-5 line-b flex items-center justify-between">
          <Link to="/" className="font-logo text-2xl md:text-3xl text-[#0A0A0A] accent-hover" data-testid="me-back-home">JOH.</Link>
          <Link to="/" className="chip text-[#4A4A4A] hover:text-[#0A0A0A] inline-flex items-center gap-2">
            <ArrowLeft size={14}/> {lang === "en" ? "Home" : "Home"}
          </Link>
        </header>
        <div className="flex-1 flex items-center justify-center px-6 py-16">
          <motion.form
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            onSubmit={signIn} className="max-w-md w-full"
            data-testid="me-signin-form"
          >
            <div className="chip text-[#4A4A4A]">— Member portal</div>
            <h1 className="font-display uppercase text-5xl md:text-7xl leading-[0.86] mt-4 text-[#0A0A0A]" data-testid="me-signin-title">
              <RevealLines lines={[lang === "en" ? "Enter" : "Log", lang === "en" ? "your gym." : "hier in."]} delay={0.15} lineDelay={0.12}/>
            </h1>
            <p className="mt-6 text-[#4A4A4A]">
              {lang === "en" ? "Enter your email to see your class pack, upcoming classes and history." : "Vul je e-mail in om je strippenkaart, komende lessen en geschiedenis te zien."}
            </p>
            <div className="mt-8 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="chip text-[#4A4A4A]">{lang === "en" ? "First name (optional)" : "Voornaam (optioneel)"}</label>
                  <input value={firstNameInput} onChange={(e)=>setFirstNameInput(e.target.value)} className="editorial-input" data-testid="me-first-name"/>
                </div>
                <div>
                  <label className="chip text-[#4A4A4A]">{lang === "en" ? "Last name (optional)" : "Achternaam (optioneel)"}</label>
                  <input value={lastNameInput} onChange={(e)=>setLastNameInput(e.target.value)} className="editorial-input" data-testid="me-last-name"/>
                </div>
              </div>
              <div>
                <label className="chip text-[#4A4A4A]">{lang === "en" ? "Email" : "E-mail"}</label>
                <input required type="email" value={emailInput} onChange={(e)=>setEmailInput(e.target.value)} className="editorial-input" data-testid="me-email"/>
              </div>
            </div>
            <button type="submit" className="btn w-full justify-center mt-8" data-testid="me-signin-btn">
              {lang === "en" ? "Open my portal" : "Open mijn portal"} <ArrowUpRight size={14}/>
            </button>
            <div className="chip text-[#4A4A4A] mt-6">
              {lang === "en" ? "No account yet?" : "Nog geen account?"}{" "}
              {trialEnabled && (
                <Link to="/#trial" className="accent-hover underline underline-offset-4">{lang === "en" ? "Book a free trial first" : "boek eerst een gratis proefles"}</Link>
              )}
            </div>
          </motion.form>
        </div>
      </main>
    );
  }

  const ent = data?.entitlement;
  const stats = data?.stats || {};
  const upcoming = data?.upcoming || [];
  const past = data?.past || [];
  const waitlist = data?.waitlist || [];

  return (
    <main className="min-h-screen bg-[#F5F3F0] grain" data-testid="me-portal">
      <header className="px-6 md:px-10 py-5 line-b flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Link to="/" className="font-logo text-2xl md:text-3xl text-[#0A0A0A] accent-hover">JOH.</Link>
          <span className="chip text-[#4A4A4A] hidden md:inline">— Member portal</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="chip text-[#4A4A4A] hidden md:inline">{member.email}</span>
          <button onClick={signOut} className="btn-ghost" data-testid="me-signout">
            <LogOut size={14}/> {lang === "en" ? "Sign out" : "Uitloggen"}
          </button>
        </div>
      </header>

      <section className="px-6 md:px-10 py-12 md:py-16">
        <div className="chip text-[#4A4A4A]">— {lang === "en" ? "Welcome back" : "Welkom terug"}</div>
        <h1 className="font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.86] mt-4 text-[#0A0A0A]" data-testid="me-hello">
          {lang === "en" ? "Hey" : "Hey"} <span className="font-serif-italic normal-case tracking-tight accent-ink">{member.name}.</span>
        </h1>
      </section>

      <section className="px-6 md:px-10 grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
        <StatCard label={lang === "en" ? "Status" : "Status"} value={
          ent?.has_membership ? (lang === "en" ? "Member" : "Member")
          : ent?.pack_credits > 0 ? (lang === "en" ? "Pack" : "Pack")
          : (lang === "en" ? "Guest" : "Gast")
        } accent={ent?.has_membership || ent?.pack_credits > 0} testid="me-stat-status"/>
        <StatCard label={lang === "en" ? "Credits left" : "Tegoed"} value={ent?.has_membership ? "∞" : (ent?.pack_credits ?? 0)} testid="me-stat-credits"/>
        <StatCard label={lang === "en" ? "Upcoming" : "Komend"} value={stats.upcoming_count ?? 0} testid="me-stat-upcoming"/>
        <StatCard label={lang === "en" ? "Attended" : "Aanwezig"} value={stats.attended ?? 0} testid="me-stat-attended"/>
        <StatCard label={lang === "en" ? "Attendance" : "Opkomst"} value={stats.attendance_pct != null ? `${stats.attendance_pct}%` : "—"} accent={(stats.attendance_pct ?? 0) >= 80} testid="me-stat-attendance"/>
      </section>

      {ent?.has_membership && (
        <MembershipUpgradeCard email={member.email} currentPlan={ent.current_plan} lang={lang}/>
      )}

      <section className="px-6 md:px-10 pb-10" data-testid="me-promo-section">
        <form onSubmit={redeemPromo} className="border border-[#0A0A0A] p-5 md:p-6 bg-[#EAE7E2] flex flex-col md:flex-row md:items-end gap-4" data-testid="me-promo-form">
          <div className="flex-1">
            <label className="chip text-[#4A4A4A]">{lang === "en" ? "Have a promo code?" : "Promocode gekregen?"}</label>
            <input
              value={promoInput}
              onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
              placeholder={lang === "en" ? "Enter code" : "Vul je code in"}
              className="editorial-input uppercase"
              data-testid="me-promo-input"
            />
          </div>
          <button type="submit" disabled={promoBusy || !promoInput.trim()} className="btn" data-testid="me-promo-submit">
            {promoBusy ? "…" : (lang === "en" ? "Redeem" : "Inwisselen")} <ArrowUpRight size={14}/>
          </button>
        </form>
      </section>

      {waitlist.length > 0 && (
        <section className="px-6 md:px-10 pb-8" data-testid="me-waitlist-bar">
          <div className="border-2 p-5 md:p-6 bg-[#F5F3F0]" style={{ borderColor: "var(--joh-accent)" }}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="chip mb-1" style={{ color: "var(--joh-accent)" }}>
                  <Users size={12} className="inline mr-1"/> {lang === "en" ? "Waitlist" : "Reservelijst"} · {waitlist.length}
                </div>
                <div className="font-display uppercase text-2xl md:text-3xl text-[#0A0A0A]">
                  {lang === "en" ? "You're on the waitlist" : "Je staat op de reservelijst"}
                </div>
                <p className="mt-2 text-sm text-[#4A4A4A] max-w-md">
                  {lang === "en"
                    ? "We'll email you the moment a spot opens. You keep your spot until 2 hours before class."
                    : "We mailen zodra er een plek vrijkomt. Je plek blijft tot 2 uur voor de les gereserveerd."}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {waitlist.slice(0, 3).map((w, i) => {
                  const d = fmtDate(w.class_date, lang);
                  return (
                    <div key={w.id || i} className="border border-[#0A0A0A] bg-[#EAE7E2] px-4 py-3 text-left" data-testid={`me-waitlist-${i}`}>
                      <div className="flex items-baseline gap-2">
                        <span className="font-display text-3xl leading-none text-[#0A0A0A]">{d.day}</span>
                        <span className="chip text-[#4A4A4A] uppercase">{d.month}</span>
                        <span className="chip ml-2" style={{ color: "var(--joh-accent)" }}>#{w.position}</span>
                      </div>
                      <div className="font-display uppercase text-sm mt-1 text-[#0A0A0A] truncate max-w-[180px]">{w.class_type}</div>
                      <div className="chip text-[#4A4A4A] mt-1">{w.class_start}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="px-6 md:px-10 pb-12">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display uppercase text-3xl md:text-5xl text-[#0A0A0A]" data-testid="me-upcoming-title">
            {lang === "en" ? "Upcoming classes" : "Komende lessen"}
          </h2>
          <Link to="/#rsvp" className="btn-ghost" data-testid="me-add-class"><ArrowUpRight size={14}/> {lang === "en" ? "Add class" : "Les toevoegen"}</Link>
        </div>
        {loading ? (
          <div className="chip text-[#4A4A4A]">{lang === "en" ? "Loading…" : "Laden…"}</div>
        ) : upcoming.length === 0 ? (
          <div className="border border-[#0A0A0A] p-8 md:p-10 text-center bg-[#EAE7E2]" data-testid="me-upcoming-empty">
            <div className="chip text-[#4A4A4A] mb-2">{lang === "en" ? "Empty" : "Nog niets"}</div>
            <p className="text-[#0A0A0A] mb-6">{lang === "en" ? "No classes booked yet. Grab a spot for this week." : "Je hebt nog geen lessen geboekt. Pak een plek voor deze week."}</p>
            <Link to="/#rsvp" className="btn">{lang === "en" ? "See classes" : "Bekijk lessen"} <ArrowUpRight size={14}/></Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="me-upcoming-grid">
            {upcoming.map((c, i) => {
              const d = fmtDate(c.class_date, lang);
              const ct = c.credit_type || "guest";
              return (
                <div key={c.id || i} className="border border-[#0A0A0A] p-5 md:p-6 bg-[#EAE7E2]" data-testid={`me-upcoming-${i}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="chip text-[#4A4A4A]">{d.weekday}</div>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="font-display text-5xl leading-none text-[#0A0A0A]">{d.day}</span>
                        <span className="chip text-[#4A4A4A] uppercase">{d.month}</span>
                      </div>
                    </div>
                    <div className="chip text-[#4A4A4A] text-right">
                      <div>{c.class_start}</div>
                      {c.class_end && <div className="opacity-70">— {c.class_end}</div>}
                    </div>
                  </div>
                  <div className="mt-6">
                    <div className="font-display uppercase text-2xl md:text-3xl leading-[0.95]">{c.class_type}</div>
                    <div className="chip text-[#4A4A4A] mt-2">{c.class_level || "—"}</div>
                  </div>
                  <div className="mt-4 flex items-center gap-2 text-sm">
                    <span className={`chip ${ct === "membership" || ct.startsWith("joh_pack") ? "accent-ink" : "text-[#4A4A4A]"}`}>{CREDIT_BADGE[ct]?.[lang] || ct}</span>
                  </div>
                  <button onClick={() => cancelRsvp(c.instance_id)} className="btn-ghost mt-4 w-full justify-center" data-testid={`me-cancel-${i}`}>
                    <X size={14}/> {lang === "en" ? "Cancel" : "Afmelden"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="px-6 md:px-10 pb-24">
        <h2 className="font-display uppercase text-3xl md:text-5xl text-[#0A0A0A] mb-6" data-testid="me-history-title">
          {lang === "en" ? "History" : "Geschiedenis"}
        </h2>
        {past.length === 0 ? (
          <div className="chip text-[#4A4A4A]" data-testid="me-history-empty">
            {lang === "en" ? "You'll see completed classes here." : "Hier verschijnen je bijgewoonde lessen."}
          </div>
        ) : (
          <div className="line-t border-[#0A0A0A]/40" data-testid="me-history-list">
            {past.map((c, i) => {
              const d = fmtDate(c.class_date, lang);
              const ct = c.credit_type || "guest";
              return (
                <div key={c.id || i} className="line-b border-[#0A0A0A]/40 py-5 grid grid-cols-12 gap-4 items-center" data-testid={`me-history-${i}`}>
                  <span className="col-span-2 md:col-span-1 chip text-[#4A4A4A]">{d.day} {d.month}</span>
                  <span className="col-span-4 md:col-span-2 chip text-[#4A4A4A]">{d.weekday}</span>
                  <span className="col-span-6 md:col-span-2 chip text-[#4A4A4A]">{c.class_start}</span>
                  <span className="col-span-8 md:col-span-4 font-display uppercase text-lg md:text-xl text-[#0A0A0A]">{c.class_type}</span>
                  <span className="col-span-4 md:col-span-1 chip text-[#4A4A4A]">{c.class_level || "—"}</span>
                  <span className={`col-span-12 md:col-span-2 chip text-right ${ct === "membership" || ct.startsWith("joh_pack") ? "accent-ink" : "text-[#4A4A4A]"}`}>
                    <Check size={12} className="inline mr-1"/>{CREDIT_BADGE[ct]?.[lang] || ct}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {(!ent?.has_membership && (ent?.pack_credits ?? 0) === 0) && (
        <section className="px-6 md:px-10 pb-24">
          <div className="border border-[#0A0A0A] p-6 md:p-10 bg-[#0A0A0A] text-[#F5F3F0] grain grain-dark flex flex-col md:flex-row items-start md:items-center gap-6 justify-between" data-testid="me-upsell">
            <div>
              <div className="chip text-[#A3A3A3]">{lang === "en" ? "No credits yet" : "Nog geen tegoed"}</div>
              <div className="font-display uppercase text-3xl md:text-5xl mt-2">
                {lang === "en" ? "Get a" : "Pak een"} <span className="accent-ink font-serif-italic normal-case tracking-tight">{lang === "en" ? "pack." : "strippenkaart."}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={bookDropin} disabled={dropinBusy} className="btn-light" data-testid="me-dropin-btn">
                {dropinBusy ? "…" : (lang === "en" ? "Book a drop-in — €15" : "Book a drop-in — €15")} <ArrowUpRight size={14}/>
              </button>
              <Link to="/join" className="btn-ghost text-[#F5F3F0] border-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A]" data-testid="me-upsell-btn">{lang === "en" ? "See plans" : "Bekijk opties"} <ArrowUpRight size={14}/></Link>
            </div>
          </div>
        </section>
      )}
    </main>
  );
}

function StatCard({ label, value, accent, testid }) {
  return (
    <div className="border border-[#0A0A0A] p-5 md:p-6 bg-[#EAE7E2]" data-testid={testid}>
      <div className="chip text-[#4A4A4A]">{label}</div>
      <div className={`font-display text-5xl md:text-6xl mt-2 ${accent ? "accent-ink" : "text-[#0A0A0A]"}`}>{value}</div>
    </div>
  );
}

const PLAN_META = {
  joh_membership_1x: { label: "1× per week", price: "€40", freq: 1 },
  joh_membership_2x: { label: "2× per week", price: "€65", freq: 2 },
  joh_membership_3x: { label: "Onbeperkt · alle lessen", price: "€80", freq: 999 },
};

function MembershipUpgradeCard({ email, currentPlan, lang }) {
  const [loading, setLoading] = useState(false);
  const cur = PLAN_META[currentPlan];
  const curFreq = cur?.freq ?? 0;
  const upgrades = Object.entries(PLAN_META).filter(([k, v]) => v.freq > curFreq);

  const openPortal = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/me/portal", {
        email,
        return_url: window.location.href,
      });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || (lang === "en" ? "Portal failed" : "Portal openen mislukt"));
      setLoading(false);
    }
  };

  return (
    <section className="px-6 md:px-10 pb-12" data-testid="me-upgrade-card">
      <div className="border border-[#0A0A0A] bg-[#EAE7E2] p-6 md:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-5">
            <div className="chip text-[#4A4A4A]">{lang === "en" ? "Your membership" : "Jouw membership"}</div>
            <div className="font-display uppercase text-3xl md:text-4xl mt-2 text-[#0A0A0A]" data-testid="me-current-plan">
              {cur ? cur.label : (lang === "en" ? "Active member" : "Actief member")}
            </div>
            {cur && (
              <div className="chip text-[#4A4A4A] mt-1">{cur.price} <span className="opacity-70">/ {lang === "en" ? "month" : "maand"}</span></div>
            )}
            <button onClick={openPortal} disabled={loading} className="btn-ghost mt-6" data-testid="me-manage-btn">
              {loading ? "…" : (lang === "en" ? "Manage subscription" : "Beheer abonnement")} <ArrowUpRight size={14}/>
            </button>
            <p className="chip text-[#4A4A4A] mt-3 max-w-xs">
              {lang === "en"
                ? "Update card, invoice, cancel or change plan via the secure Stripe portal."
                : "Kaart, facturen, opzeggen of wisselen van plan via de beveiligde Stripe portal."}
            </p>
          </div>

          {upgrades.length > 0 && (
            <div className="lg:col-span-7">
              <div className="chip mb-3" style={{ color: "var(--joh-accent)" }}>
                {lang === "en" ? "Train more?" : "Meer trainen?"}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {upgrades.map(([k, v]) => (
                  <button
                    key={k}
                    onClick={openPortal}
                    disabled={loading}
                    className="text-left border border-[#0A0A0A] bg-[#F5F3F0] p-4 hover:bg-[#0A0A0A] hover:text-[#F5F3F0] transition-colors group"
                    data-testid={`me-upgrade-${k}`}
                  >
                    <div className="flex items-baseline justify-between gap-4">
                      <span className="chip text-current opacity-70">{v.label}</span>
                      <span className="font-display text-2xl">{v.price}</span>
                    </div>
                    <div className="mt-2 chip inline-flex items-center gap-1" style={{ color: "var(--joh-accent)" }}>
                      {lang === "en" ? "Upgrade" : "Upgrade"} <ArrowUpRight size={12}/>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
